/**
 * Кодировщик ленты в ESC/POS-байты (cp866) для термопринтеров.
 * Вход — строки из receiptLines: раскладка и транспорт разделены,
 * агент устройства просто скармливает эти байты в порт принтера.
 */

const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;
const CODEPAGE_CP866 = 17; // Epson: ESC t 17 = PC866 Cyrillic

/** Символы вне cp866 → печатаемая замена (тенге шире на 1 знак — приемлемо на ленте). */
const FALLBACK: Record<string, string> = {
  /*
   * Тенге — ровно ОДИН знак. Замена «тг» делала денежную строку
   * на символ длиннее ширины ленты, и принтер заворачивал хвостовую
   * «г» на следующую строку: под каждой суммой чека висела сиротская
   * буква. Раскладка считает символы честно — замена обязана тоже
   */
  '₸': 'T', '×': 'x', '…': '..', '−': '-', '—': '-', '«': '"', '»': '"',
  '·': '.', '∙': '.', '\u00a0': ' ',
  // бейджи блюд и значки интерфейса уезжают на ленту в именах и заметках:
  // кухне нужно слово, а не вопросительный знак
  '🌶': 'остро', '🌱': 'веган', '✨': 'NEW', '★': '*', '•': '-',
  '✓': 'v', '✔': 'v', '✎': '!', '↩': '<-', '→': '->', '←': '<-',
  '°': ' гр', '–': '-', '’': "'", '“': '"', '”': '"',
};

export function toCp866(ch: string): number | null {
  const c = ch.codePointAt(0)!;
  if (c < 0x80) return c;                      // ASCII
  if (c >= 0x0410 && c <= 0x042f) return 0x80 + (c - 0x0410); // А-Я
  if (c >= 0x0430 && c <= 0x043f) return 0xa0 + (c - 0x0430); // а-п
  if (c >= 0x0440 && c <= 0x044f) return 0xe0 + (c - 0x0440); // р-я
  if (c === 0x0401) return 0xf0;               // Ё
  if (c === 0x0451) return 0xf1;               // ё
  if (c === 0x2116) return 0xfc;               // №
  if (c === 0x2500) return 0xc4;               // ─ (линия-разделитель)
  if (c === 0x2550) return 0xcd;               // ═ (тяжёлая линия итога)
  /*
   * Казахские буквы: в cp866 их нет, и «Рақмет» печатался как
   * «Ра?мет». Транслитерация в ближайшую кириллицу честнее
   * вопросительного знака — гость читает слово, а не ребус
   */
  const KZ: Record<number, number> = {
    0x04d8: 0x80, 0x04d9: 0xa0, // Ә ә → А а
    0x0492: 0x83, 0x0493: 0xa3, // Ғ ғ → Г г
    0x049a: 0x8a, 0x049b: 0xaa, // Қ қ → К к
    0x04a2: 0x8d, 0x04a3: 0xad, // Ң ң → Н н
    0x04e8: 0x8e, 0x04e9: 0xae, // Ө ө → О о
    0x04b0: 0x93, 0x04b1: 0xe3, // Ұ ұ → У у
    0x04ae: 0x93, 0x04af: 0xe3, // Ү ү → У у
    0x04ba: 0x95, 0x04bb: 0xe5, // Һ һ → Х х
    0x0406: 0x88, 0x0456: 0xa8, // І і → И и
  };
  if (KZ[c] !== undefined) return KZ[c]!;
  return null;
}

export function encodeLine(line: string): number[] {
  const out: number[] = [];
  for (const ch of line) {
    const direct = toCp866(ch);
    if (direct !== null) { out.push(direct); continue; }
    const fb = FALLBACK[ch] ?? '?';
    for (const f of fb) out.push(toCp866(f) ?? 0x3f);
  }
  return out;
}

/** QR (GS ( k, model 2): выбор модели → размер → коррекция → данные → печать. */
export function escposQr(data: string, moduleSize = 6): number[] {
  const payload = [...data].map((ch) => ch.codePointAt(0)! & 0x7f); // URL — ASCII
  const len = payload.length + 3;
  const pL = len & 0xff;
  const pH = (len >> 8) & 0xff;
  return [
    ESC, 0x61, 0x01,                               // по центру
    GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00, // модель 2
    GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, moduleSize, // размер модуля
    GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, 0x31,       // коррекция M
    GS, 0x28, 0x6b, pL, pH, 0x31, 0x50, 0x30, ...payload, // данные
    GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30,       // печать
    ESC, 0x61, 0x00,                               // выравнивание назад
  ];
}

/** Полный чек: init → кодовая страница → строки → [QR] → протяжка → отрез. */
export function escposEncode(lines: string[], opts?: { qr?: string }): Uint8Array {
  const bytes: number[] = [
    ESC, 0x40,                 // init
    ESC, 0x74, CODEPAGE_CP866, // select cp866
  ];
  for (const line of lines) {
    bytes.push(...encodeLine(line), LF);
  }
  if (opts?.qr) bytes.push(LF, ...escposQr(opts.qr));
  bytes.push(ESC, 0x64, 0x04); // feed 4 lines
  bytes.push(GS, 0x56, 0x42, 0x00); // partial cut
  return Uint8Array.from(bytes);
}

/**
 * Открыть денежный ящик.
 *
 * Ящик подключается к принтеру, а не к компьютеру: принтер даёт
 * импульс на разъём RJ-11, и замок щёлкает. Команда та же почти
 * у всех моделей — ESC p, номер контакта и две длительности импульса.
 *
 * Контакт 0 — первый разъём, он же единственный у большинства касс.
 * Длительности в единицах по 2 мс: 50 и 250 — общепринятые значения,
 * при меньших замок иногда не срабатывает, при больших щёлкает громко
 * и греется катушка.
 */
export function escposOpenDrawer(pin: 0 | 1 = 0): Uint8Array {
  return new Uint8Array([ESC, 0x70, pin, 50, 250]);
}
